import React from 'react';

import type { UUID } from '@aline/common';
import { Breadcrumbs, Link } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  current: {
    color: theme.palette.text.primary,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Lets both crumbs shrink and truncate instead of wrapping onto a second line.
  item: {
    minWidth: 0,
  },
  link: {
    display: 'block',
    fontSize: 12,
    maxWidth: '100%',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    verticalAlign: 'bottom',
    whiteSpace: 'nowrap',
  },
  root: {
    '& ol': {
      flexWrap: 'nowrap',
    },
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
}));

interface TaskBreadcrumbsProps {
  onOpenTask: (taskId: UUID) => void;
  parentTask: { id: UUID; title: string };
  title: string;
  className?: string;
}

/** A sub-task's parent, linked, then the sub-task itself. */
export function TaskBreadcrumbs({
  className,
  onOpenTask,
  parentTask,
  title,
}: TaskBreadcrumbsProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <Breadcrumbs
      aria-label="Task breadcrumb"
      className={className}
      classes={{ li: classes.item, root: classes.root }}
      separator={<ChevronRight size={14} />}
    >
      <Link
        className={classes.link}
        component="button"
        title={parentTask.title}
        underline="hover"
        onClick={() => onOpenTask(parentTask.id)}
      >
        {parentTask.title || 'Untitled'}
      </Link>
      <span aria-current="page" className={classes.current} title={title}>
        {title || 'Untitled'}
      </span>
    </Breadcrumbs>
  );
}
